import { useCallback, useEffect, useMemo, useState } from 'react'
import { Icon } from '@iconify/react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ApiClientError } from '@/lib/api/client'
import { resolveCampaignContactWhatsappHref } from '@/lib/campaign-contact'
import { resolveLandingSocialLinks } from '@/lib/campaign-social-links'
import { fetchLandingCampaign } from '@/lib/landing'
import { shouldSkipPublicPageRedirectOnCustomDomain } from '@/lib/campaign-custom-domain'
import { useCustomDomainSlug } from '@/contexts/CustomDomainCampaignContext'
import { applyLandingDocumentMeta } from '@/lib/landing-meta'
import { initializeGoogleCampaignTracking } from '@/lib/google-tag'
import { initializeMetaPixel } from '@/lib/meta-pixel'
import { landingFetchDonorMe, type LandingDonor } from '@/lib/landing-auth'
import {
  matchesPublicCampaignTemplate,
  TEMPLATE_2_PUBLIC_CAMPAIGN_TEMPLATE_KEY,
  TEMPLATE_3_PUBLIC_CAMPAIGN_TEMPLATE_KEY,
} from '@/features/publicCampaignTemplates/registry'
import { PublicCampaignThemeStyle } from '@/features/publicCampaignTemplates/theme'
import { PublicCampaignTemplate3Landing } from '@/features/publicCampaignTemplates/template3'
import { getCircularAvatarUrl, getInstitutionBrandImageUrl } from '@/lib/profile-media'
import type {
  LandingCampaign,
  LandingCampaignImage,
  LandingCampaignResponse,
  LandingInstitution,
  LandingPremiatedNumberItem,
} from '@/types/landing'
import { createLandingEmbedResizeReporter, escapeLandingEmbedToAppPath } from '@/lib/landingEmbedResize'
import { LandingDefaultFooter } from '@/components/Landing/LandingDefaultFooter'
import { SafeLandingHtml } from '@/components/SafeLandingHtml'
import { htmlHasVisibleText, stripHtmlToPlainText } from '@/lib/landing-html'
import { LandingFooterSocialLinks } from '@/components/Landing/LandingFooterSocialLinks/LandingFooterSocialLinks'
import { PUBLIC_LEGAL_LINKS } from '@/lib/public-legal-links'
import { safePublicHttpHref } from '@/lib/public-href'
import { LandingPageInlineWidgetCheckout } from '@/components/Landing/LandingPageInlineWidgetCheckout'
import { LandingSalesClosedNotice } from '@/components/Landing/LandingSalesClosedNotice'
import { LandingSalesClosedModal } from '@/components/Landing/LandingSalesClosedModal'
import { isCampaignOpenForSales, shouldShowSalesClosedWinnerModal } from '@/lib/campaign-sales'
import { readLandingDonorToken, writeLandingDonorToken } from '@/lib/landing-donor-session'
import { useLandingCampaignPageView } from '@/hooks/useLandingCampaignPageView'
import template2Styles from '@/features/publicCampaignTemplates/template2/PublicCampaignTemplate2.module.css'
import styles from './LandingPage.module.css'
import { LandingPremiatedNumbersExpandable } from './LandingPremiatedNumbersExpandable'

const FAVORITES_STORAGE_KEY = 'legalprime:landingFavorites'

type GalleryItem = {
  key: string
  src: string
  alt: string
}

function formatCurrency(value: number | string | null | undefined): string {
  if (value === null || value === undefined) return 'R$ 0,00'
  const parsed = typeof value === 'string' ? Number(value) : value
  if (Number.isNaN(parsed)) return 'R$ 0,00'

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(parsed)
}

function toSafeNumber(value: number | string | null | undefined): number {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0
  if (typeof value === 'string') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : 0
  }

  return 0
}

function clampQuantity(value: number, minQty: number, maxQty: number | null): number {
  if (maxQty !== null) {
    return Math.min(Math.max(value, minQty), maxQty)
  }

  return Math.max(value, minQty)
}

function readFavoriteIds(): number[] {
  if (typeof window === 'undefined') return []

  try {
    const stored = window.localStorage.getItem(FAVORITES_STORAGE_KEY)
    if (!stored) return []
    const parsed = JSON.parse(stored)
    if (!Array.isArray(parsed)) return []
    return parsed.filter((value): value is number => typeof value === 'number')
  } catch {
    return []
  }
}

function writeFavoriteIds(ids: number[]): void {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(ids))
}

function buildGalleryItems(campaign: LandingCampaign): GalleryItem[] {
  const items: GalleryItem[] = []

  if (campaign.main_image_url) {
    items.push({
      key: 'main',
      src: campaign.main_image_url,
      alt: stripHtmlToPlainText(campaign.title) || campaign.name || 'Imagem da campanha',
    })
  }

  if (Array.isArray(campaign.campaign_images)) {
    campaign.campaign_images.forEach((image: LandingCampaignImage) => {
      if (!image.image_url) return
      if (items.some((item) => item.src === image.image_url)) return

      items.push({
        key: `gallery-${image.id}`,
        src: image.image_url,
        alt: `${stripHtmlToPlainText(campaign.title) || campaign.name} ${image.position + 1}`,
      })
    })
  }

  return items
}

function buildPublicPageRedirectUrl(targetUrl: string, currentSearchParams: URLSearchParams): string {
  const url = new URL(targetUrl, window.location.origin)

  currentSearchParams.forEach((value, key) => {
    if (!url.searchParams.has(key)) {
      url.searchParams.append(key, value)
    }
  })

  return url.toString()
}

function buildInstitutionName(institution: LandingInstitution): string {
  if (institution.person_type === 'pj') {
    return institution.corporate_name || institution.name
  }

  return institution.name || `${institution.first_name || ''} ${institution.last_name || ''}`.trim()
}

function buildLandingStepUrl(
  slug: string,
  step: 'conta' | 'checkout' | 'perfil',
  options?: {
    quantity?: number
    affiliateCode?: string
    returnMode?: 'checkout' | 'account'
  }
): string {
  const params = new URLSearchParams()

  if (step === 'conta' && options?.returnMode) {
    params.set('return', options.returnMode)
  }
  if (options?.quantity) {
    params.set('quantity', String(options.quantity))
  }
  if (options?.affiliateCode) {
    params.set('af', options.affiliateCode)
  }

  const suffix = params.toString() ? `?${params.toString()}` : ''
  return `/campanha/${slug}/${step}${suffix}`
}

export function LandingPage() {
  const navigate = useNavigate()
  const { slug } = useParams<{ slug: string }>()
  const customDomainSlug = useCustomDomainSlug()
  const [searchParams] = useSearchParams()
  const [data, setData] = useState<LandingCampaignResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const [selectedImageIndex, setSelectedImageIndex] = useState(0)
  const [selectedQuantity, setSelectedQuantity] = useState(1)
  const [detailsOpen, setDetailsOpen] = useState(true)
  const [regulationOpen, setRegulationOpen] = useState(false)
  const [awardedTitlesOpen, setAwardedTitlesOpen] = useState(false)
  const [premiatedListOpen, setPremiatedListOpen] = useState(false)
  const [isFavorite, setIsFavorite] = useState(false)
  const [donorToken, setDonorToken] = useState<string | null>(readLandingDonorToken)
  const [donor, setDonor] = useState<LandingDonor | null>(null)
  const [salesClosedModalOpen, setSalesClosedModalOpen] = useState(false)

  useEffect(() => {
    if (!slug) {
      setError('Slug da campanha não fornecido.')
      setIsLoading(false)
      return
    }

    let mounted = true

    const loadCampaign = async () => {
      setIsLoading(true)
      setError(null)

      try {
        const response = await fetchLandingCampaign(slug)
        if (!mounted) return
        setData(response)
      } catch (err) {
        if (!mounted) return

        if (err instanceof ApiClientError) {
          setError(err.status === 404 ? 'Campanha não encontrada.' : err.message || 'Erro ao carregar campanha.')
        } else {
          setError('Erro ao carregar campanha. Tente novamente mais tarde.')
        }
      } finally {
        if (mounted) setIsLoading(false)
      }
    }

    loadCampaign()
    return () => { mounted = false }
  }, [slug])

  const campaign = data?.data?.campaign ?? null
  const institution = data?.data?.institution ?? null
  useLandingCampaignPageView(campaign?.id ?? null)
  const donationOnly = campaign?.generation_status === 'donation_only'
  const premiatedNumberItems = useMemo((): LandingPremiatedNumberItem[] => {
    const raw = data?.data?.premiated_numbers
    if (!Array.isArray(raw)) return []
    return raw.filter(
      (row): row is LandingPremiatedNumberItem =>
        row !== null &&
        typeof row === 'object' &&
        typeof (row as LandingPremiatedNumberItem).number === 'string' &&
        (row as LandingPremiatedNumberItem).number.trim() !== ''
    )
  }, [data])
  const showPremiatedNumbersEntry = premiatedNumberItems.length > 0
  const regulationContentType = campaign?.regulation_content_type === 'text' ? 'text' : 'document'
  const regulationText = campaign?.regulation_text ?? ''
  const regulationPdfHref = safePublicHttpHref(campaign?.regulation_pdf_url)
  const whatsappButtonHref = safePublicHttpHref(campaign?.whatsapp_button_url)
  const hasRegulationContent =
    regulationContentType === 'text'
      ? htmlHasVisibleText(regulationText)
      : Boolean(regulationPdfHref)
  const galleryItems = useMemo(() => (campaign ? buildGalleryItems(campaign) : []), [campaign])
  const pricePerNumber = useMemo(() => toSafeNumber(campaign?.price), [campaign?.price])
  const minQty = useMemo(() => Math.max(1, campaign?.min_quantity_per_purchase ?? 1), [campaign?.min_quantity_per_purchase])
  const maxQty = useMemo(() => {
    const value = campaign?.max_quantity_per_purchase ?? null
    if (value === null || value === undefined) return null
    return value >= minQty ? value : minQty
  }, [campaign?.max_quantity_per_purchase, minQty])
  const affiliateCode = searchParams.get('af')?.trim() || undefined
  const isEmbedMode = searchParams.get('embed') === '1'

  const closeSalesClosedModal = useCallback(() => {
    setSalesClosedModalOpen(false)
  }, [])

  useEffect(() => {
    setSalesClosedModalOpen(shouldShowSalesClosedWinnerModal(campaign))
  }, [campaign])

  useEffect(() => {
    if (!regulationOpen || typeof window === 'undefined') return

    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [regulationOpen])

  useEffect(() => {
    if (campaign) {
      applyLandingDocumentMeta(campaign)
    }
  }, [campaign])

  useEffect(() => {
    if (!isEmbedMode || !slug || typeof window === 'undefined') return

    const reporter = createLandingEmbedResizeReporter(slug, campaign?.id)
    reporter.schedule()

    return () => {
      reporter.dispose()
    }
  }, [isEmbedMode, slug, campaign, detailsOpen, regulationOpen, awardedTitlesOpen, premiatedListOpen, menuOpen])

  useEffect(() => {
    if (isEmbedMode || !campaign?.public_page_url || typeof window === 'undefined') return
    if (shouldSkipPublicPageRedirectOnCustomDomain(customDomainSlug, campaign.slug)) return

    const redirectUrl = buildPublicPageRedirectUrl(campaign.public_page_url, searchParams)
    const currentUrl = new URL(window.location.href)

    if (redirectUrl === currentUrl.toString()) {
      return
    }

    window.location.replace(redirectUrl)
  }, [campaign, customDomainSlug, searchParams, isEmbedMode])

  useEffect(() => {
    initializeMetaPixel(campaign?.fb_pixel)
  }, [campaign?.fb_pixel])

  useEffect(() => {
    initializeGoogleCampaignTracking({
      measurementId: campaign?.gtm_measurement_id ?? null,
      googleTagManagerContainerId: campaign?.g_pixel ?? null,
    })
  }, [campaign?.gtm_measurement_id, campaign?.g_pixel])

  const openAccountSubpage = useCallback((mode: 'checkout' | 'account') => {
    if (!slug) return
    const path = buildLandingStepUrl(slug, 'conta', {
      returnMode: mode,
      quantity: mode === 'checkout' ? selectedQuantity : undefined,
      affiliateCode,
    })
    if (isEmbedMode && escapeLandingEmbedToAppPath(path)) {
      return
    }
    navigate(path)
  }, [affiliateCode, isEmbedMode, navigate, selectedQuantity, slug])

  const openProfilePage = useCallback(() => {
    if (!slug) return
    const path = buildLandingStepUrl(slug, 'perfil', {
      affiliateCode,
    })
    if (isEmbedMode && escapeLandingEmbedToAppPath(path)) {
      return
    }
    navigate(path)
  }, [affiliateCode, isEmbedMode, navigate, slug])

  const refreshDonorContext = useCallback(async (tokenValue: string, campaignId: number) => {
    try {
      const donorMe = await landingFetchDonorMe(tokenValue, campaignId)

      setDonor(donorMe)
    } catch {
      writeLandingDonorToken(null)
      setDonorToken(null)
      setDonor(null)
    }
  }, [])

  useEffect(() => {
    if (!campaign) return
    setSelectedQuantity(clampQuantity(minQty, minQty, maxQty))
    setSelectedImageIndex(0)
    setIsFavorite(readFavoriteIds().includes(campaign.id))
  }, [campaign, minQty, maxQty])

  useEffect(() => {
    if (!campaign || !institution || !donorToken) return
    void refreshDonorContext(donorToken, campaign.id)
  }, [campaign, institution, donorToken, refreshDonorContext])

  useEffect(() => {
    if (!galleryItems.length) return
    if (selectedImageIndex <= galleryItems.length - 1) return
    setSelectedImageIndex(0)
  }, [galleryItems, selectedImageIndex])

  const handleAccountAction = useCallback(() => {
    if (isEmbedMode) {
      openAccountSubpage('account')
      return
    }
    if (donorToken && donor) {
      openProfilePage()
      return
    }
    openAccountSubpage('account')
  }, [donorToken, donor, isEmbedMode, openAccountSubpage, openProfilePage])

  const handleFavoriteToggle = useCallback(() => {
    if (!campaign) return

    const current = new Set(readFavoriteIds())
    if (current.has(campaign.id)) {
      current.delete(campaign.id)
      setIsFavorite(false)
    } else {
      current.add(campaign.id)
      setIsFavorite(true)
    }

    writeFavoriteIds(Array.from(current))
  }, [campaign])

  if (isLoading) {
    return (
      <div
        className={
          isEmbedMode
            ? `${styles.loadingContainer} ${styles.loadingContainerEmbed}`
            : styles.loadingContainer
        }
      >
        <div className={styles.loadingSpinner}>
          <Icon icon="mdi:loading" className={styles.spinnerIcon} />
        </div>
        <p className={styles.loadingText}>Carregando campanha...</p>
      </div>
    )
  }

  if (error || !campaign || !institution) {
    return (
      <div
        className={
          isEmbedMode
            ? `${styles.errorContainer} ${styles.errorContainerEmbed}`
            : styles.errorContainer
        }
      >
        <div className={styles.errorContent}>
          <Icon icon="mdi:alert-circle-outline" className={styles.errorIcon} />
          <h1 className={styles.errorTitle}>Ops! Algo deu errado</h1>
          <p className={styles.errorMessage}>{error || 'Campanha não encontrada.'}</p>
          <button type="button" className={styles.retryButton} onClick={() => window.location.reload()}>
            <Icon icon="mdi:refresh" />
            Tentar novamente
          </button>
        </div>
      </div>
    )
  }

  const title = campaign.title?.trim() || campaign.name
  const titlePlain = stripHtmlToPlainText(title)
  const highlightedDescription = campaign.description?.trim() || null
  const institutionName = buildInstitutionName(institution)
  const institutionAvatarUrl = getCircularAvatarUrl(institution)
  const institutionBrandUrl = getInstitutionBrandImageUrl(institution)
  const selectedImage = galleryItems[selectedImageIndex]
  const contactWhatsappHref = resolveCampaignContactWhatsappHref({
    contactValue: campaign.public_contact_url,
    campaignTitle: titlePlain,
    whatsappButtonUrl: campaign.whatsapp_button_url,
    institutionPhone: institution.phone,
  })
  const landingSocialLinks = resolveLandingSocialLinks({
    campaignLinks: campaign.public_social_links,
    institutionLinks: institution.social_links,
  })

  const winnerHref = slug ? `/campanha/${slug}/ganhador` : null
  const showWinnerButton = Boolean(campaign.draw_winner?.winner && winnerHref)
  const showAwardedTitlesCard = Boolean(campaign.public_awarded_titles_html?.trim())
  const showDetails = Boolean(campaign.public_details_html?.trim())
  const salesOpen = isCampaignOpenForSales(campaign)
  if (matchesPublicCampaignTemplate(campaign.public_template_key, TEMPLATE_3_PUBLIC_CAMPAIGN_TEMPLATE_KEY)) {
    return (
      <PublicCampaignTemplate3Landing
        campaign={campaign}
        institution={institution}
        institutionName={institutionName}
        institutionBrandUrl={institutionBrandUrl}
        institutionAvatarUrl={institutionAvatarUrl}
        title={title}
        highlightedDescription={highlightedDescription}
        pricePerNumber={pricePerNumber}
        donationOnly={donationOnly}
        salesOpen={salesOpen}
        isEmbedMode={isEmbedMode}
        contactWhatsappHref={contactWhatsappHref}
        landingSocialLinks={landingSocialLinks}
        premiatedNumberItems={premiatedNumberItems}
        showPremiatedNumbersEntry={showPremiatedNumbersEntry}
        hasRegulationContent={hasRegulationContent}
        regulationContentType={regulationContentType}
        regulationText={regulationText}
        isFavorite={isFavorite}
        salesClosedModalOpen={salesClosedModalOpen}
        regulationOpen={regulationOpen}
        premiatedListOpen={premiatedListOpen}
        onCloseSalesClosedModal={closeSalesClosedModal}
        onAccountAction={handleAccountAction}
        onFavoriteToggle={handleFavoriteToggle}
        onRegulationOpenChange={setRegulationOpen}
        onPremiatedListOpenChange={setPremiatedListOpen}
      />
    )
  }

  if (matchesPublicCampaignTemplate(campaign.public_template_key, TEMPLATE_2_PUBLIC_CAMPAIGN_TEMPLATE_KEY)) {
    return (
      <div
        className={
          isEmbedMode
            ? `${template2Styles.page} ${template2Styles.pageEmbed}`
            : template2Styles.page
        }
      >
        <PublicCampaignThemeStyle campaign={campaign} />

        <header className={template2Styles.topbar}>
          <div className={template2Styles.topbarInner}>
            <a href="#top" className={template2Styles.brand}>
              {institutionBrandUrl ? (
                <img src={institutionBrandUrl} alt={institutionName} className={template2Styles.brandLogo} />
              ) : (
                <div className={template2Styles.brandFallback}>{institutionName.slice(0, 1)}</div>
              )}
              <div className={template2Styles.brandText}>
                <span className={template2Styles.brandEyebrow}>Campanha pública</span>
                <strong className={template2Styles.brandTitle}>{institutionName}</strong>
              </div>
            </a>

            <nav className={template2Styles.nav}>
              <button type="button" className={template2Styles.navButton} onClick={handleAccountAction}>Minha conta</button>
              <a
                href={contactWhatsappHref}
                className={template2Styles.navLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                Contato
              </a>
            </nav>

            <button
              type="button"
              className={`${template2Styles.menuButton} ${menuOpen ? template2Styles.menuButtonActive : ''}`}
              aria-label={menuOpen ? 'Fechar menu' : 'Abrir menu'}
              aria-expanded={menuOpen}
              aria-controls="landing-mobile-menu-template2"
              onClick={() => setMenuOpen((current) => !current)}
            >
              <Icon icon={menuOpen ? 'mdi:close' : 'mdi:menu'} />
            </button>
          </div>

          {menuOpen && (
            <nav
              id="landing-mobile-menu-template2"
              className={template2Styles.mobileMenu}
              aria-label="Menu da campanha"
            >
              <div className={template2Styles.mobileMenuPanel}>
                <button
                  type="button"
                  className={template2Styles.mobileMenuPrimary}
                  onClick={() => {
                    setMenuOpen(false)
                    handleAccountAction()
                  }}
                >
                  <Icon icon="mdi:account-circle-outline" aria-hidden />
                  Minha conta
                </button>
                <a
                  href={contactWhatsappHref}
                  className={template2Styles.mobileMenuSecondary}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMenuOpen(false)}
                >
                  <Icon icon="mdi:whatsapp" aria-hidden />
                  Contato
                </a>
              </div>
            </nav>
          )}
        </header>

        <main className={template2Styles.surface} id="top">
          <section className={template2Styles.hero}>
            <div>
              <div className={template2Styles.heroMedia}>
                {selectedImage ? (
                  <>
                    <div className={template2Styles.badge}>Compra imediata</div>
                    <img src={selectedImage.src} alt={selectedImage.alt} />
                    <div className={template2Styles.heroOverlay} />
                  </>
                ) : (
                  <div className={template2Styles.emptyState}>
                    <Icon icon="mdi:image-outline" />
                    <p>Imagem principal indisponível.</p>
                  </div>
                )}
              </div>

              {galleryItems.length > 1 && (
                <div className={template2Styles.thumbRow}>
                  {galleryItems.map((item, index) => (
                    <button
                      key={item.key}
                      type="button"
                      className={`${template2Styles.thumbButton} ${index === selectedImageIndex ? template2Styles.thumbButtonActive : ''}`}
                      onClick={() => setSelectedImageIndex(index)}
                      aria-label={`Ver imagem ${index + 1}`}
                    >
                      <img src={item.src} alt={item.alt} />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className={template2Styles.stack}>
              <div className={template2Styles.card}>
                <span className={template2Styles.eyebrow}>Campanha pública</span>
                <SafeLandingHtml html={title} as="h1" variant="inline" className={template2Styles.title} />
                {highlightedDescription && (
                  <SafeLandingHtml
                    html={highlightedDescription}
                    as="div"
                    variant="block"
                    className={template2Styles.text}
                  />
                )}

                <div className={template2Styles.inlineMeta}>
                  <div className={template2Styles.metaChip}>
                    <span>Avaliação</span>
                    <strong>
                      {campaign.public_reviews_text ? (
                        <SafeLandingHtml html={campaign.public_reviews_text} as="span" variant="inline" />
                      ) : (
                        '5 estrelas'
                      )}
                    </strong>
                  </div>
                  <div className={template2Styles.metaChip}>
                    <span>Status</span>
                    <strong>
                      {campaign.public_live_badge_text ? (
                        <SafeLandingHtml html={campaign.public_live_badge_text} as="span" variant="inline" />
                      ) : (
                        'Ativa agora'
                      )}
                    </strong>
                  </div>
                  <div className={template2Styles.metaChip}>
                    <span>Responsável</span>
                    <strong>{institutionName}</strong>
                  </div>
                </div>

                <div className={template2Styles.statsGrid}>
                  <div className={template2Styles.statCard}>
                    <span>Compra segura</span>
                    <strong>Protegida</strong>
                  </div>
                  <div className={template2Styles.statCard}>
                    <span>Entrega</span>
                    <strong>Instantânea</strong>
                  </div>
                  <div className={template2Styles.statCard}>
                    <span>Participação</span>
                    <strong>100% online</strong>
                  </div>
                </div>
              </div>

              <div className={template2Styles.panel}>
                <div className={template2Styles.pricePanel}>
                  <div className={template2Styles.priceBlock}>
                    <span>{donationOnly ? 'Valor por contribuição' : 'Valor por número'}</span>
                    <strong>{formatCurrency(pricePerNumber).replace('R$', '').trim()}</strong>
                  </div>

                  {salesOpen ? (
                    <LandingPageInlineWidgetCheckout campaign={campaign} />
                  ) : (
                    <LandingSalesClosedNotice campaign={campaign} visualVariant="dark" />
                  )}

                  <div className={template2Styles.actionGroup}>
                    <button type="button" className={template2Styles.actionSecondary} onClick={handleFavoriteToggle}>
                      <Icon icon={isFavorite ? 'mdi:heart' : 'mdi:heart-outline'} />
                      {isFavorite ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
                    </button>
                    {hasRegulationContent && (
                      <button type="button" className={template2Styles.actionSecondary} onClick={() => setRegulationOpen(true)}>
                        <Icon icon="mdi:file-document-outline" />
                        Ver regulamento
                      </button>
                    )}
                    {showAwardedTitlesCard && (
                      <button type="button" className={template2Styles.actionSecondary} onClick={() => setAwardedTitlesOpen(true)}>
                        <Icon icon="mdi:trophy-outline" />
                        Títulos premiados
                      </button>
                    )}
                    {showWinnerButton && winnerHref && (
                      <a href={winnerHref} className={template2Styles.actionSecondary}>
                        <Icon icon="mdi:trophy-award" />
                        Ver ganhador
                      </a>
                    )}
                  </div>
                </div>
              </div>

              {showPremiatedNumbersEntry && (
                <LandingPremiatedNumbersExpandable
                  items={premiatedNumberItems}
                  donationOnly={donationOnly}
                  visualVariant="dark"
                  expanded={premiatedListOpen}
                  onExpandedChange={setPremiatedListOpen}
                />
              )}

              {htmlHasVisibleText(campaign.public_notice_text) && (
                <SafeLandingHtml html={campaign.public_notice_text} as="div" variant="block" className={template2Styles.notice} />
              )}

              {showDetails && (
                <div className={template2Styles.card}>
                  <button type="button" className={template2Styles.actionSecondary} onClick={() => setDetailsOpen((current) => !current)}>
                    <Icon icon={detailsOpen ? 'mdi:chevron-up' : 'mdi:chevron-down'} />
                    {detailsOpen ? 'Ocultar descrição detalhada' : 'Mostrar descrição detalhada'}
                  </button>
                  {detailsOpen && (
                    <SafeLandingHtml
                      html={campaign.public_details_html}
                      as="div"
                      variant="block"
                      className={template2Styles.modalHtml}
                    />
                  )}
                </div>
              )}
            </div>
          </section>
        </main>

        <footer className={template2Styles.footerInner} id="landing-footer">
          <div className={template2Styles.footerGrid}>
            <section className={template2Styles.footerBlock} id="institution-section">
              <h2>Responsável pelo sorteio</h2>
              <p>Empresa: {institutionName}</p>
              {institution.document_cnpj && <p>CNPJ: {institution.document_cnpj}</p>}
              {institution.email && <p>E-mail: {institution.email}</p>}
              {institution.phone && <p>Telefone: {institution.phone}</p>}
            </section>

            <section className={template2Styles.footerBlock}>
              <h2>Siga-nos</h2>
              <LandingFooterSocialLinks links={landingSocialLinks} />
            </section>

            <section className={template2Styles.footerBlock}>
              <h2>Informações</h2>
              <div className={template2Styles.stack}>
                <a href={PUBLIC_LEGAL_LINKS.privacy} target="_blank" rel="noopener noreferrer">Política de Privacidade</a>
                <a href={PUBLIC_LEGAL_LINKS.terms} target="_blank" rel="noopener noreferrer">Termos e Condições de Uso</a>
                <a
                  href={contactWhatsappHref}
                  className={template2Styles.footerActionButton}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Contato
                </a>
                <button type="button" className={template2Styles.footerActionButton} onClick={handleAccountAction}>
                  Minha conta
                </button>
              </div>
            </section>
          </div>

          <div className={template2Styles.footerBottom}>© 2026 SORTEIOS.COM.BR. Todos os direitos reservados.</div>
        </footer>

        {whatsappButtonHref && (
          <a href={whatsappButtonHref} target="_blank" rel="noopener noreferrer" className={template2Styles.floatingButton} aria-label="Falar no WhatsApp">
            <Icon icon="mdi:whatsapp" />
          </a>
        )}

        {(regulationOpen || awardedTitlesOpen) && (
          <div
            className={`${template2Styles.modalOverlay} ${regulationOpen ? template2Styles.modalOverlayRegulationTop : ''}`}
          >
            {regulationOpen && (
              <div className={template2Styles.modalCard}>
                <div className={template2Styles.modalHeader}>
                  <div>
                    <h2>Regulamento</h2>
                    <p>Leia as regras e condições da campanha.</p>
                  </div>
                  <button type="button" className={template2Styles.modalClose} onClick={() => setRegulationOpen(false)}>
                    <Icon icon="mdi:close" />
                  </button>
                </div>
                {regulationContentType === 'text' ? (
                  <SafeLandingHtml html={regulationText} as="div" variant="block" className={template2Styles.modalText} />
                ) : (
                  <>
                    <div className={template2Styles.modalBody}>
                      <iframe title="Regulamento da campanha" src={regulationPdfHref ?? undefined} className={template2Styles.modalFrame} />
                    </div>
                    <div className={template2Styles.modalActions}>
                      <a href={regulationPdfHref ?? '#'} target="_blank" rel="noopener noreferrer" className={template2Styles.navButton}>Abrir em nova aba</a>
                    </div>
                  </>
                )}
              </div>
            )}

            {awardedTitlesOpen && (
              <div className={template2Styles.modalCard}>
                <div className={template2Styles.modalHeader}>
                  <div>
                    <h2>Títulos premiados</h2>
                    <p>{donationOnly ? 'Veja os ganhadores desta campanha.' : 'Veja os números e ganhadores desta campanha.'}</p>
                  </div>
                  <button type="button" className={template2Styles.modalClose} onClick={() => setAwardedTitlesOpen(false)}>
                    <Icon icon="mdi:close" />
                  </button>
                </div>
                <SafeLandingHtml
                  html={campaign.public_awarded_titles_html}
                  as="div"
                  variant="block"
                  className={template2Styles.modalHtml}
                />
              </div>
            )}
          </div>
        )}

        <LandingSalesClosedModal open={salesClosedModalOpen} onClose={closeSalesClosedModal} />
      </div>
    )
  }

  return (
    <div
      className={
        isEmbedMode ? `${styles.landingPage} ${styles.landingPageEmbed}` : styles.landingPage
      }
    >
      <PublicCampaignThemeStyle campaign={campaign} />

      <header className={styles.topbar}>
        <div className={styles.topbarInner}>
          <a href="#top" className={styles.brand}>
            {institutionAvatarUrl ? (
              <img src={institutionAvatarUrl} alt={institutionName} className={styles.brandLogo} />
            ) : (
              <div className={styles.brandFallback}>{institutionName.slice(0, 1)}</div>
            )}
          </a>

          <nav className={styles.desktopNav}>
            <button type="button" className={styles.topCta} onClick={handleAccountAction}>Minha Conta</button>
            <a
              href={contactWhatsappHref}
              className={styles.navLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              Contato
            </a>
          </nav>

          <div className={styles.mobileActions}>
            <button
              type="button"
              className={`${styles.iconButton} ${menuOpen ? styles.iconButtonActive : ''}`}
              aria-label={menuOpen ? 'Fechar menu' : 'Abrir menu'}
              aria-expanded={menuOpen}
              aria-controls="landing-mobile-menu"
              onClick={() => setMenuOpen((current) => !current)}
            >
              <Icon icon={menuOpen ? 'mdi:close' : 'mdi:menu'} />
            </button>
          </div>
        </div>

        {menuOpen && (
          <nav id="landing-mobile-menu" className={styles.mobileMenu} aria-label="Menu da campanha">
            <div className={styles.mobileMenuPanel}>
              <button
                type="button"
                className={styles.mobileMenuPrimary}
                onClick={() => {
                  setMenuOpen(false)
                  handleAccountAction()
                }}
              >
                <Icon icon="mdi:account-circle-outline" aria-hidden />
                Minha Conta
              </button>
              <a
                href={contactWhatsappHref}
                className={styles.mobileMenuSecondary}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMenuOpen(false)}
              >
                <Icon icon="mdi:whatsapp" aria-hidden />
                Contato
              </a>
            </div>
          </nav>
        )}
      </header>

      <main className={styles.mainContent} id="top">
        <div className={styles.productGrid}>
          <section className={styles.galleryPanel}>
            <div className={styles.heroImageFrame}>
              {selectedImage ? <img src={selectedImage.src} alt={selectedImage.alt} className={styles.heroImage} /> : <div className={styles.imageFallback}><Icon icon="mdi:image-outline" /></div>}
            </div>

            {galleryItems.length > 1 && (
              <div className={styles.thumbnailRow}>
                {galleryItems.map((item, index) => (
                  <button key={item.key} type="button" className={`${styles.thumbnailButton} ${index === selectedImageIndex ? styles.thumbnailButtonActive : ''}`} onClick={() => setSelectedImageIndex(index)} aria-label={`Ver imagem ${index + 1}`}>
                    <img src={item.src} alt={item.alt} className={styles.thumbnailImage} />
                  </button>
                ))}
              </div>
            )}

            <div className={styles.trustGrid}>
              <div className={styles.trustCard}><Icon icon="mdi:shield-check-outline" /><span>Compra Segura</span></div>
              <div className={styles.trustCard}><Icon icon="mdi:clock-fast" /><span>Instantâneo</span></div>
            </div>
          </section>

          <section className={styles.purchasePanel}>
            <div className={styles.productHeader}>
              <SafeLandingHtml html={title} as="h1" variant="inline" className={styles.productTitle} />
            </div>

            <div className={styles.purchasePanelBody}>
              {hasRegulationContent && (
                <>
                  <hr className={styles.productHeaderDivider} />
                  <div className={styles.productHeaderActions}>
                    <button
                      type="button"
                      className={styles.regulationButton}
                      onClick={() => setRegulationOpen(true)}
                    >
                      <Icon icon="mdi:file-document-outline" />
                      Ver Regulamento
                    </button>
                  </div>
                </>
              )}

              {(campaign.public_reviews_text || campaign.public_live_badge_text) && (
                <div className={styles.socialProofRow}>
                  {campaign.public_reviews_text && (
                    <div className={styles.reviewsBox}>
                      <div className={styles.stars}>{Array.from({ length: 5 }).map((_, index) => <Icon key={index} icon="mdi:star" />)}</div>
                      <span>
                        <SafeLandingHtml html={campaign.public_reviews_text} as="span" variant="inline" />
                      </span>
                    </div>
                  )}
                  {campaign.public_live_badge_text && (
                    <div className={styles.liveBadge}>
                      <span className={styles.liveDot} />
                      <span>
                        <SafeLandingHtml html={campaign.public_live_badge_text} as="span" variant="inline" />
                      </span>
                    </div>
                  )}
                </div>
              )}

            {showPremiatedNumbersEntry && (
              <LandingPremiatedNumbersExpandable
                items={premiatedNumberItems}
                donationOnly={donationOnly}
                visualVariant="light"
                expanded={premiatedListOpen}
                onExpandedChange={setPremiatedListOpen}
              />
            )}

            {showAwardedTitlesCard && (
              <button type="button" className={styles.awardedTitlesCard} onClick={() => setAwardedTitlesOpen(true)}>
                <div className={styles.awardedTitlesInfo}>
                  <Icon icon="mdi:trophy-outline" />
                  <div>
                    <strong>Títulos Premiados</strong>
                    <span>{donationOnly ? 'Veja os ganhadores' : 'Veja os números e ganhadores'}</span>
                  </div>
                </div>
                <span className={styles.awardedTitlesAction}>Ver Títulos</span>
              </button>
            )}

            {salesOpen ? (
              <div className={styles.urgencyCard}>
                <Icon icon="mdi:timer-outline" />
                <div>
                  <strong>Aproveite agora mesmo!</strong>
                  <span>Promoção por tempo limitado!</span>
                </div>
              </div>
            ) : null}

            <div className={styles.priceCard}>
              <div className={styles.priceCardShine} aria-hidden />
              <div className={styles.priceCardContent}>
                <span className={styles.priceEyebrow}>Por apenas</span>
                <div className={styles.priceAmountRow}>
                  <span className={styles.priceCurrency}>R$</span>
                  <strong className={styles.priceValue}>
                    {formatCurrency(pricePerNumber).replace('R$', '').trim()}
                  </strong>
                </div>
                <span className={styles.priceSuffix}>
                  {donationOnly ? 'por contribuição' : 'por número'}
                </span>
              </div>
            </div>

            {salesOpen ? (
              <LandingPageInlineWidgetCheckout campaign={campaign} />
            ) : (
              <LandingSalesClosedNotice campaign={campaign} />
            )}

            <div className={styles.actionStack}>
              <button type="button" className={styles.secondaryAction} onClick={handleFavoriteToggle}>
                <Icon icon={isFavorite ? 'mdi:heart' : 'mdi:heart-outline'} />
                {isFavorite ? 'Remover dos Favoritos' : 'Adicionar aos Favoritos'}
              </button>
              {showWinnerButton && winnerHref && (
                <a href={winnerHref} className={styles.secondaryAction}>
                  <Icon icon="mdi:trophy-award" />
                  Ver ganhador
                </a>
              )}
            </div>

            {htmlHasVisibleText(campaign.public_notice_text) && (
              <SafeLandingHtml html={campaign.public_notice_text} as="div" variant="block" className={styles.noticeText} />
            )}

            {showDetails && (
              <div className={styles.detailsSection}>
                <button type="button" className={styles.detailsToggle} onClick={() => setDetailsOpen((current) => !current)}>
                  <span>Descrição Detalhada</span>
                  <Icon icon={detailsOpen ? 'mdi:chevron-up' : 'mdi:chevron-down'} />
                </button>
                {detailsOpen && (
                  <SafeLandingHtml
                    html={campaign.public_details_html}
                    as="div"
                    variant="block"
                    className={styles.detailsHtml}
                  />
                )}
              </div>
            )}

            {highlightedDescription && !showDetails && (
              <SafeLandingHtml html={highlightedDescription} as="div" variant="block" className={styles.inlineDescription} />
            )}
            </div>
          </section>
        </div>
      </main>

      <LandingDefaultFooter
        campaignTitle={titlePlain}
        institution={institution}
        institutionName={institutionName}
        institutionAvatarUrl={institutionAvatarUrl}
        socialLinks={landingSocialLinks}
        contactWhatsappHref={contactWhatsappHref}
        onAccountAction={handleAccountAction}
      />

      {whatsappButtonHref && (
        <a href={whatsappButtonHref} target="_blank" rel="noopener noreferrer" className={styles.whatsappButton} aria-label="Falar no WhatsApp">
          <Icon icon="mdi:whatsapp" />
        </a>
      )}

      {(regulationOpen || awardedTitlesOpen) && (
        <div className={`${styles.modalOverlay} ${regulationOpen ? styles.modalOverlayRegulationTop : ''}`}>
          {regulationOpen && (
            <div className={styles.modalCard}>
              <div className={styles.modalHeader}>
                <div>
                  <h2>Regulamento</h2>
                  <p>Leia as regras e condições da campanha.</p>
                </div>
                <button type="button" className={styles.modalClose} onClick={() => setRegulationOpen(false)}>
                  <Icon icon="mdi:close" />
                </button>
              </div>
              {regulationContentType === 'text' ? (
                <SafeLandingHtml html={regulationText} as="div" variant="block" className={`${styles.modalHtml} ${styles.modalTextContent}`} />
              ) : (
                <>
                  <div className={styles.modalBody}>
                    <iframe title="Regulamento da campanha" src={regulationPdfHref ?? undefined} className={styles.modalFrame} />
                  </div>
                  <div className={styles.modalFooter}>
                    <a href={regulationPdfHref ?? '#'} target="_blank" rel="noopener noreferrer" className={styles.modalPrimaryLink}>Abrir em nova aba</a>
                  </div>
                </>
              )}
            </div>
          )}

          {awardedTitlesOpen && (
            <div className={styles.modalCard}>
              <div className={styles.modalHeader}>
                <div>
                  <h2>Títulos Premiados</h2>
                  <p>{donationOnly ? 'Veja os ganhadores desta campanha.' : 'Veja os números e ganhadores desta campanha.'}</p>
                </div>
                <button type="button" className={styles.modalClose} onClick={() => setAwardedTitlesOpen(false)}>
                  <Icon icon="mdi:close" />
                </button>
              </div>
              <SafeLandingHtml
                html={campaign.public_awarded_titles_html}
                as="div"
                variant="block"
                className={styles.modalHtml}
              />
            </div>
          )}
        </div>
      )}

      <LandingSalesClosedModal open={salesClosedModalOpen} onClose={closeSalesClosedModal} />
    </div>
  )
}
